import React, { useState, useEffect } from 'react';
import {
  Container,
  Copy,
  Heading,
  Stack,
  Button,
  Flex,
  Squish,
  Square,
  Link,
  Row,
  Column,
  Loader,
} from '@grubhubprod/gh-cookbook-react/dist';
import {
  Breakpoint,
  useDeviceSize,
} from '@grubhubprod/umami-library/dist/hooks/useDeviceSize';
import { SignupStepFooter } from 'src/components/common/features/Signup/components/SignupStepFooter/SignupStepFooter';
import { defaultUploadStatus } from 'src/components/CSVUploadTracking/CSVUploadTracking.utils';
import { getWindow } from 'src/utilities/window';
import { ISignupStepProps } from 'src/components/common/features/Signup/components/SignupProgressTracker/SignupProgressTrackerStep/SignupProgressTrackerStep';
import { ManagePersonModal } from 'src/components/ManagePersonModal/ManagePersonModal';
import { useSignupStatus } from 'src/components/selfService/features/Signup/context/SignupStatusProvider/SignupStatusProvider';
import { ROLES } from 'src/types/Employee';
import { useDinerSearch, SearchStatus } from 'src/utilities/useSearchHooks';
import { PeopleTable } from 'src/components/PeopleTable/PeopleTable';
import { AnalyticsEventLabel } from 'src/types/Analytics';
import { getConfig } from 'src/utilities/config';
import { useMealCreditEntityName } from 'src/components/selfService/common/hooks/useMealCreditEntityName';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';
import { AddEmployeesOptionButton } from 'src/components/AddEmployeesOptionButton/AddEmployeesOptionButton';
import { CSVUploadTracking } from 'src/components/CSVUploadTracking/CSVUploadTracking';
import { useGetQueryParams } from 'src/utilities/useGetQueryParams';

enum PeopleStepSection {
  SELECTION,
  CSV,
  INDIVIDUAL,
}

export function PeopleStep({
  updateMobileStepSize,
}: ISignupStepProps): JSX.Element {
  const { breakpoint } = useDeviceSize(getWindow());
  const { corpId, goToNextStep, isWelcomeBackModalPresent } = useSignupStatus();
  const isMobile = breakpoint < Breakpoint.sm;

  const [currentSection, setCurrentSection] = useState<PeopleStepSection>();
  const [uploadedDinerCount, setUploadedDinerCount] = useState<number>(0);

  const {
    totalResults: totalDiners,
    searchStatus,
    doSearch: getCorpDiners,
  } = useDinerSearch(corpId);

  const { sendTealiumPageView } = useAnalytics();

  useEffect(() => {
    if (
      !isWelcomeBackModalPresent &&
      currentSection === PeopleStepSection.SELECTION
    ) {
      // send analytics
      sendTealiumPageView({
        pageGroup: 'corporate self service - sign up flow',
        pageName: 'add people - select method',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWelcomeBackModalPresent, currentSection]);

  useEffect(() => {
    if (corpId) {
      // we don't want to include the admin in the diner count
      getCorpDiners({ diner_role: ROLES.DINER });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpId]);

  useEffect(() => {
    if (searchStatus === SearchStatus.COMPLETE) {
      if (totalDiners > 0) {
        setCurrentSection(PeopleStepSection.INDIVIDUAL);
      } else {
        setCurrentSection(PeopleStepSection.SELECTION);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchStatus]);

  useEffect(() => {
    if (updateMobileStepSize) {
      updateMobileStepSize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSection]);

  function updateSection(section: PeopleStepSection): void {
    setCurrentSection(section);
  }

  function onPartialLoaded(dinersCreatedCount: number) {
    setUploadedDinerCount(dinersCreatedCount);
  }

  function renderCurrentSection(): JSX.Element {
    switch (currentSection) {
      case PeopleStepSection.SELECTION: {
        return (
          <PeopleStepSelectionSection
            isMobile={isMobile}
            updateSection={updateSection}
            uploadedDinerCount={uploadedDinerCount}
          />
        );
      }
      case PeopleStepSection.CSV: {
        return (
          <CsvUploadSection
            isMobile={isMobile}
            updateMobileStepSize={updateMobileStepSize}
            updateSection={updateSection}
            corpId={corpId}
            goToNextStep={goToNextStep}
            partialLoadedCb={onPartialLoaded}
            uploadedDinerCount={uploadedDinerCount}
          />
        );
      }
      case PeopleStepSection.INDIVIDUAL: {
        return (
          <AddIndividuallySection
            isMobile={isMobile}
            updateMobileStepSize={updateMobileStepSize}
            updateSection={updateSection}
          />
        );
      }
      default: {
        return null;
      }
    }
  }

  return (
    <Container variant="sm">
      <Square inset="x-sm">{renderCurrentSection()}</Square>
    </Container>
  );
}

interface IPeopleStepSectionBaseProps {
  isMobile: boolean;
  updateSection?: (section: PeopleStepSection) => void;
}

interface IPeopleStepSectionProps extends IPeopleStepSectionBaseProps {
  updateMobileStepSize?: () => void;
}

interface IPeopleStepSelectionSection extends IPeopleStepSectionBaseProps {
  uploadedDinerCount: number;
}

function PeopleStepSelectionSection({
  isMobile,
  updateSection,
  uploadedDinerCount,
}: IPeopleStepSelectionSection): JSX.Element {
  const { sendTealiumEvent } = useAnalytics();
  const { goToNextStep, goToPreviousStep, sendBackButtonAnalytics } =
    useSignupStatus();
  const isUpdating = useGetQueryParams('isUpdating');
  const { MEAL_CREDIT_ENTITY_NAME: entityName } = useMealCreditEntityName();

  const [isUpdatingStep] = useState(isUpdating);
  const configuration = getConfig();

  const onSkip = () => {
    sendTealiumEvent({
      eventcategory: 'self-service sign up_people',
      eventaction: 'add_people_skip',
      eventlabel: `skip_${AnalyticsEventLabel.SUCCESS}`,
    });
    goToNextStep();
  };
  return (
    <>
      <Stack y={isMobile ? 'xxx-sm' : 'sm'}>
        <Heading variant="h2" textAlign={isMobile ? 'left' : 'center'}>
          Who is this {entityName.singular} for?
        </Heading>
      </Stack>
      <Stack y={isMobile ? 'md' : 'x-lg'}>
        <Copy variant="body" textAlign={isMobile ? 'left' : 'center'}>
          <div>
            Upload a list of the people you want to feed or create one with our{' '}
            <Link
              href={`${configuration.assetsURL}/csv-upload-template.csv`}
              download="csv-upload-template.csv"
            >
              template
            </Link>
            . You can also add people individually or skip this step and do it
            later from your account.
          </div>
        </Copy>
      </Stack>
      <Container variant="xs">
        <Row gutter={24}>
          <Column gutter={24} tablet={6}>
            <Stack y="sm">
              <AddEmployeesOptionButton
                buttonImage="https://media-cdn.grubhub.com/image/upload/v1606232463/corporate/CSV_Illustration.svg"
                buttonCopy="Upload CSV"
                onClick={() => {
                  updateSection(PeopleStepSection.CSV);
                }}
              />
            </Stack>
          </Column>
          <Column gutter={24} tablet={6}>
            <Stack y="sm">
              <AddEmployeesOptionButton
                buttonImage="https://media-cdn.grubhub.com/image/upload/v1606232460/corporate/Computer_Illustration.svg"
                buttonCopy="Add individually"
                onClick={() => {
                  updateSection(PeopleStepSection.INDIVIDUAL);
                }}
              />
            </Stack>
          </Column>
        </Row>
      </Container>
      <SignupStepFooter>
        <Flex justifyContent={isUpdatingStep ? 'flex-end' : 'space-between'}>
          {!isUpdatingStep && (
            <Stack
              x={isMobile ? 'x-sm' : 'none'}
              width={isMobile ? '100%' : 'auto'}
            >
              <Button
                variant={isMobile ? 'secondary' : 'tertiary'}
                buttonSize="medium"
                block={isMobile}
                onClick={() => {
                  sendBackButtonAnalytics();
                  goToPreviousStep();
                }}
              >
                Back
              </Button>
            </Stack>
          )}
          {uploadedDinerCount && uploadedDinerCount > 0 ? (
            <Button
              variant="primary"
              buttonSize="medium"
              block={isMobile}
              onClick={goToNextStep}
            >
              Save and continue
            </Button>
          ) : (
            <Button
              variant={isMobile ? 'secondary' : 'tertiary'}
              buttonSize="medium"
              block={isMobile}
              onClick={onSkip}
            >
              Skip for now
            </Button>
          )}
        </Flex>
      </SignupStepFooter>
    </>
  );
}

export interface ICsvUploadSection extends IPeopleStepSectionProps {
  onSuccess?: () => void;
  onError?: () => void;
  corpId: string;
  goToNextStep?: () => void;
  partialLoadedCb?: (dinersCreatedCount: number) => void;
  uploadedDinerCount?: number;
}

export function CsvUploadSection({
  isMobile,
  updateSection,
  updateMobileStepSize,
  corpId,
  goToNextStep,
  partialLoadedCb,
  uploadedDinerCount = 0,
}: ICsvUploadSection): JSX.Element {
  const isUpdating = useGetQueryParams('isUpdating');

  const [isUpdatingStep] = useState(isUpdating);
  const {
    resultsList: diners,
    totalResults: totalDiners,
    searchStatus,
    hasMoreResults,
    loadMoreNumber,
    doSearch: getCorpDiners,
  } = useDinerSearch(corpId);

  const [currentUploadStatus, setCurrentUploadStatus] =
    useState(defaultUploadStatus);

  const [acceptedCsvFile, setAcceptedCsvFile] = useState<File>(null);
  const { sendTealiumPageView, sendTealiumEvent } = useAnalytics();

  const { sendBackButtonAnalytics, isWelcomeBackModalPresent } =
    useSignupStatus();

  useEffect(() => {
    // send analytics
    if (!isWelcomeBackModalPresent) {
      sendTealiumPageView({
        pageGroup: 'corporate self service - sign up flow',
        pageName: 'add people - upload csv',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWelcomeBackModalPresent]);

  useEffect(() => {
    if (!acceptedCsvFile && updateMobileStepSize) {
      // update mobile size when file is reset back to null
      updateMobileStepSize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptedCsvFile]);

  useEffect(() => {
    if (isMobile && (currentUploadStatus.hasError || diners.length > 0)) {
      updateMobileStepSize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diners, currentUploadStatus]);

  function getPrimaryButtonText(): string {
    if (isUpdatingStep) {
      return 'Update';
    }

    if (isMobile) {
      return 'Next';
    }

    return 'Save and continue';
  }

  function onCSVUploadSuccess() {
    // we don't want to include the admin in the diner count
    getCorpDiners({ diner_role: ROLES.DINER });
    sendTealiumEvent({
      eventcategory: 'self-service sign up_people',
      eventaction: 'add people_upload csv',
      eventlabel: AnalyticsEventLabel.SUCCESS,
    });
  }

  function onCSVUploadError(analyticsErrorsList: string[] = []) {
    sendTealiumEvent({
      eventcategory: 'self-service sign up_people',
      eventaction: 'add people_upload csv',
      eventlabel: analyticsErrorsList.join(','),
    });
  }

  return (
    <>
      <Stack y="xxx-sm">
        <Heading variant="h2" textAlign="left">
          Upload CSV file
        </Heading>
      </Stack>
      <Stack y="sm">
        <CSVUploadTracking
          acceptedCsvFileCb={(acceptedCsvFile: File) =>
            setAcceptedCsvFile(acceptedCsvFile)
          }
          uploadedRecordsTotalCb={partialLoadedCb}
          corpId={corpId}
          onSuccess={onCSVUploadSuccess}
          onError={onCSVUploadError}
          showTryAgainBtn
          updateCurrentUploadStatusCb={(statusProps) =>
            setCurrentUploadStatus(statusProps)
          }
        />
      </Stack>
      {!acceptedCsvFile && isMobile && (
        <Button
          variant="tertiary"
          onClick={goToNextStep}
          className="removeButtonPadding"
        >
          <Copy variant="body" color="interactive">
            Skip for now
          </Copy>
        </Button>
      )}
      {acceptedCsvFile &&
        currentUploadStatus.hasError &&
        uploadedDinerCount > 0 && (
          <Squish inset="lg" color="success">
            {uploadedDinerCount === 1 ? (
              <span>1 row was uploaded successfully</span>
            ) : (
              <span>{uploadedDinerCount} rows were uploaded successfully</span>
            )}
          </Squish>
        )}
      {acceptedCsvFile && diners.length > 0 && (
        <>
          <Flex justifyContent="flex-end">
            <Copy variant="body" color="secondaryText">
              {diners.length} of {totalDiners} people
            </Copy>
          </Flex>
          <PeopleTable
            isSignupTable
            diners={diners}
            bodyHeight="350px"
            searchStatus={searchStatus}
            hasMoreResults={hasMoreResults}
            loadMoreNumber={loadMoreNumber}
            getCorpDiners={getCorpDiners}
            corpId={corpId}
          />
        </>
      )}
      <SignupStepFooter>
        <Flex justifyContent="space-between" alignItems="center">
          <Stack
            x={isMobile ? 'x-sm' : 'none'}
            width={isMobile ? '100%' : 'auto'}
          >
            <Button
              variant={isMobile ? 'secondary' : 'tertiary'}
              buttonSize="medium"
              block={isMobile}
              onClick={() => {
                sendBackButtonAnalytics();
                updateSection(PeopleStepSection.SELECTION);
              }}
            >
              Back
            </Button>
          </Stack>
          <Flex justifyContent="flex-end" width={isMobile ? '100%' : 'auto'}>
            {!isMobile && !acceptedCsvFile && uploadedDinerCount < 1 && (
              <Button
                variant={isMobile ? 'secondary' : 'tertiary'}
                buttonSize="medium"
                block={isMobile}
                onClick={goToNextStep}
              >
                Skip for now
              </Button>
            )}
            <Button
              variant="primary"
              buttonSize="medium"
              block={isMobile}
              disabled={
                uploadedDinerCount < 1 && !currentUploadStatus.isSuccessful
              }
              onClick={goToNextStep}
            >
              {getPrimaryButtonText()}
            </Button>
          </Flex>
        </Flex>
      </SignupStepFooter>
    </>
  );
}

function AddIndividuallySection({
  isMobile,
  updateSection,
  updateMobileStepSize,
}: IPeopleStepSectionProps): JSX.Element {
  const {
    corpId,
    goToNextStep,
    sendBackButtonAnalytics,
    isWelcomeBackModalPresent,
  } = useSignupStatus();
  const isUpdating = useGetQueryParams('isUpdating');
  const { MEAL_CREDIT_ENTITY_NAME: entityName } = useMealCreditEntityName();

  const [isUpdatingStep] = useState(isUpdating);
  const [showPeopleModal, setShowPeopleModal] = useState(false);
  const [isFirstLoadComplete, setIsFirstLoadComplete] = useState(false);

  const {
    resultsList: diners,
    totalResults: totalDiners,
    searchStatus,
    hasMoreResults,
    loadMoreNumber,
    doSearch: getCorpDiners,
  } = useDinerSearch(corpId);

  const { sendTealiumPageView } = useAnalytics();

  useEffect(() => {
    if (!isWelcomeBackModalPresent) {
      // send analytics
      sendTealiumPageView({
        pageGroup: 'corporate self service - sign up flow',
        pageName: 'add people - add manually',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWelcomeBackModalPresent]);

  useEffect(() => {
    // we don't want to include the admin in the diner count
    getCorpDiners({ diner_role: ROLES.DINER });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isFirstLoadComplete && searchStatus === SearchStatus.COMPLETE) {
      setIsFirstLoadComplete(true);

      if (updateMobileStepSize) {
        updateMobileStepSize();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchStatus, isFirstLoadComplete]);

  function togglePeopleModal() {
    setShowPeopleModal(!showPeopleModal);
  }

  function onPersonUpdateSuccess() {
    togglePeopleModal();
    // we don't want to include the admin in the diner count
    getCorpDiners({ diner_role: ROLES.DINER, page: 1 });
  }

  function getPrimaryButtonText(): string {
    if (isUpdatingStep) {
      return 'Update';
    }

    if (isMobile) {
      return 'Next';
    }

    return 'Save and continue';
  }

  function DefaultFooter({
    isAddManuallyScreen,
  }: {
    isAddManuallyScreen?: boolean;
  }): JSX.Element {
    const showBackButton = isAddManuallyScreen || !isUpdatingStep;

    return (
      <SignupStepFooter>
        <Flex justifyContent={!showBackButton ? 'flex-end' : 'space-between'}>
          {showBackButton && (
            <Stack
              x={isMobile ? 'x-sm' : 'none'}
              width={isMobile ? '100%' : 'auto'}
            >
              <Button
                variant={isMobile ? 'secondary' : 'tertiary'}
                buttonSize="medium"
                block={isMobile}
                onClick={() => {
                  sendBackButtonAnalytics();
                  updateSection(PeopleStepSection.SELECTION);
                }}
              >
                Back
              </Button>
            </Stack>
          )}
          <Button
            variant={isMobile ? 'secondary' : 'tertiary'}
            buttonSize="medium"
            block={isMobile}
            onClick={goToNextStep}
          >
            Skip for now
          </Button>
        </Flex>
      </SignupStepFooter>
    );
  }

  if (!isFirstLoadComplete && searchStatus !== SearchStatus.COMPLETE) {
    return (
      <>
        <Flex alignItems="center" justifyContent="center">
          <Loader size="md" />
        </Flex>
        <DefaultFooter />
      </>
    );
  }

  return (
    <>
      {diners.length > 0 ? (
        <>
          <Stack y="sm">
            <Heading variant="h2">Add people</Heading>
          </Stack>
          <Flex justifyContent="flex-end">
            <Copy variant="body" color="secondaryText">
              {diners.length} of {totalDiners} people
            </Copy>
          </Flex>
          <PeopleTable
            isSignupTable
            bodyHeight="400px"
            diners={diners}
            searchStatus={searchStatus}
            hasMoreResults={hasMoreResults}
            loadMoreNumber={loadMoreNumber}
            getCorpDiners={getCorpDiners}
            corpId={corpId}
          />
          <SignupStepFooter>
            <Flex
              justifyContent={isUpdatingStep ? 'flex-end' : 'space-between'}
            >
              {!isUpdatingStep && (
                <Stack
                  x={isMobile ? 'x-sm' : 'none'}
                  width={isMobile ? '100%' : 'auto'}
                >
                  <Button
                    variant={isMobile ? 'secondary' : 'tertiary'}
                    buttonSize="medium"
                    block={isMobile}
                    onClick={() => {
                      sendBackButtonAnalytics();
                      updateSection(PeopleStepSection.SELECTION);
                    }}
                  >
                    Back
                  </Button>
                </Stack>
              )}
              <Button
                variant="primary"
                buttonSize="medium"
                block={isMobile}
                disabled={diners.length === 0}
                onClick={goToNextStep}
              >
                {getPrimaryButtonText()}
              </Button>
            </Flex>
          </SignupStepFooter>
        </>
      ) : (
        <>
          <Stack y="sm">
            <Heading variant="h2" textAlign="center">
              Let's start adding people
            </Heading>
          </Stack>
          <Stack y="x-lg">
            <Copy variant="body" textAlign="center">
              <div>
                You'll need the full name and email for each person you want to
                add to your {entityName.singular}.
              </div>
            </Copy>
          </Stack>
          <Container variant="xs">
            <Button variant="primary" block onClick={togglePeopleModal}>
              Add people
            </Button>
          </Container>
          <DefaultFooter isAddManuallyScreen />
          {showPeopleModal && (
            <ManagePersonModal
              corpId={corpId}
              closeModal={togglePeopleModal}
              onSuccess={onPersonUpdateSuccess}
            />
          )}
        </>
      )}
    </>
  );
}
